/**
 * The dock — the screener's cell, holding one or two panels.
 *
 * `Layout.dock` in the contract; the design is `docs/plans/split-view.md`.
 * Everything here is pure, for `layout-templates.ts`'s reason: the interesting
 * logic must not live in the one place this app cannot test.
 *
 * **Split is not a flag.** Two panels showing *is* split on, one is split off,
 * none is the dock hidden. A stored boolean beside the list would be a second
 * answer to the same question, and the first close would falsify it.
 */

import type { Dock, DockPanel, Layout } from "@qufolio/model";

import type { ScreenerPlacement } from "./layout-templates";

/** How many panels the web app shows at once. The contract allows more. */
export const DOCK_MAX = 2;

/**
 * The order Split fills the second half in: the strip's own order, top to
 * bottom. Data sits above the screener keys, so turning Split on beside the
 * screener opens the data panel — the only other panel today.
 */
export const PANEL_ORDER: readonly DockPanel[] = ["data", "screener", "community", "ai"];

/** A dock nobody has touched: the screener alone, as every layout was. */
export const DEFAULT_DOCK: Dock = { panels: ["screener"] };

/** The layout's dock, with an absent one read as the screener alone. */
export function dockOf(layout: Layout | undefined): Dock {
  return layout?.dock ?? DEFAULT_DOCK;
}

export function isSplit(dock: Dock): boolean {
  return dock.panels.length >= 2;
}

export function isShowing(dock: Dock, panel: DockPanel): boolean {
  return dock.panels.includes(panel);
}

/**
 * Sizes are kept only while they still describe the panels.
 *
 * A share belongs to a *pair*, so any change to which panels show drops it and
 * the new pair starts equal. Keeping a stale pair's share would hand the data
 * panel the screener's 70% the moment one replaced the other.
 */
function withPanels(panels: DockPanel[], from: Dock): Dock {
  // `pinned` is about the strip, not the dock's panels, so it survives every
  // change of what the dock shows — or a panel switch would silently unpin.
  return from.pinned ? { panels, pinned: from.pinned } : { panels };
}

/**
 * Show a panel.
 *
 * One showing: it **replaces** that one — the data panel's swap, generalised.
 * Two showing: it replaces the **second**, so the first stays where the user
 * put it. Already showing: nothing changes.
 */
export function openPanel(dock: Dock, panel: DockPanel): Dock {
  if (isShowing(dock, panel)) return dock;
  if (dock.panels.length < DOCK_MAX) return withPanels([panel], dock);
  return withPanels([dock.panels[0]!, panel], dock);
}

/**
 * Show a panel **beside** what is there — the second half, never a swap.
 *
 * What a panel coming home from its own window does: it left the dock to be
 * read elsewhere, and returning should not take the screener's place.
 */
export function addPanel(dock: Dock, panel: DockPanel): Dock {
  if (isShowing(dock, panel)) return dock;
  if (dock.panels.length === 0) return withPanels([panel], dock);
  return withPanels([dock.panels[0]!, panel], dock);
}

/** Close a panel. The other one, if any, takes the whole dock. */
export function closePanel(dock: Dock, panel: DockPanel): Dock {
  if (!isShowing(dock, panel)) return dock;
  return withPanels(dock.panels.filter((p) => p !== panel), dock);
}

/** A panel's key: close it if it is showing, open it if not. */
export function togglePanel(dock: Dock, panel: DockPanel): Dock {
  return isShowing(dock, panel) ? closePanel(dock, panel) : openPanel(dock, panel);
}

/**
 * The Split key.
 *
 * On: the next panel in strip order that is not showing joins as the second.
 * A hidden dock comes back as the screener with that panel beside it, because
 * a split of one is not a split. Off: the second panel closes.
 */
export function toggleSplit(dock: Dock): Dock {
  if (isSplit(dock)) return withPanels([dock.panels[0]!], dock);
  const first = dock.panels[0] ?? "screener";
  const second = PANEL_ORDER.find((p) => p !== first);
  return second ? withPanels([first, second], dock) : withPanels([first], dock);
}

/** Swap the two panels — and their shares with them. */
export function swapPanels(dock: Dock): Dock {
  if (!isSplit(dock)) return dock;
  const panels = [...dock.panels].reverse();
  return {
    panels,
    ...(dock.sizes ? { sizes: [...dock.sizes].reverse() } : {}),
    ...(dock.pinned ? { pinned: dock.pinned } : {}),
  };
}

/** Record a dragged share. Ignored unless it has one size per panel showing. */
export function resizeDock(dock: Dock, sizes: string[]): Dock {
  if (sizes.length !== dock.panels.length || sizes.length < 2) return dock;
  return { ...dock, sizes };
}

/** The shares to lay out with: the stored ones, or equal (50 / 50). */
export function dockSizes(dock: Dock): string[] {
  const { panels, sizes } = dock;
  return sizes && sizes.length === panels.length ? sizes : panels.map(() => "1fr");
}

/**
 * Which way the dock splits: **across its long side**.
 *
 * Beside the charts the dock is tall, so its panels stack (`column`); above or
 * below them it is wide, so they sit side by side (`row`).
 */
export function dockDirection(placement: ScreenerPlacement): "row" | "column" {
  return placement === "left" || placement === "right" ? "column" : "row";
}

/** The smallest a panel may be dragged to, in px. AG Grid given 0 never recovers. */
export const DOCK_MIN_PX = 120;

/**
 * Two pixel extents to a pair of `fr` shares, the smaller clamped to the minimum.
 *
 * Rounded to three places so a drag does not write eighteen digits of noise
 * into the workspace; `fr` only cares about the ratio.
 */
export function sharesFromPixels(first: number, second: number, min = DOCK_MIN_PX): string[] {
  const total = first + second;
  if (!(total > 0)) return ["1fr", "1fr"];
  const floor = Math.min(min, total / 2);
  const a = Math.min(Math.max(first, floor), total - floor);
  const b = total - a;
  const round = (n: number) => `${Math.round((n / total) * 2 * 1000) / 1000}fr`;
  return [round(a), round(b)];
}

/** A strip panel the layout keeps pinned open (`Dock.pinned`). */
export type StripPin = NonNullable<Dock["pinned"]>[number];

export function isPinned(dock: Dock, pin: StripPin): boolean {
  return dock.pinned?.includes(pin) ?? false;
}

/**
 * Pin or unpin a strip panel. Unpinning the last drops the key rather than
 * storing `[]`: qv-data collapses an empty container to absent, and a save
 * must equal its reload.
 */
export function togglePin(dock: Dock, pin: StripPin): Dock {
  const rest = (dock.pinned ?? []).filter((p) => p !== pin);
  const pinned = isPinned(dock, pin) ? rest : [...rest, pin];
  const { pinned: _old, ...base } = dock;
  return pinned.length ? { ...base, pinned } : base;
}
